import React from 'react';
import {
  Compass,
  Bookmark,
  Layers,
  Search,
  MapPin,
  Coins,
  CalendarDays,
} from 'lucide-react';
import { Language, ViewMode } from '../types/spot';

interface HeaderProps {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  savedCount: number;
  onOpenCurrencyConverter: () => void;
  onOpenWeekendPlanner: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  viewMode,
  setViewMode,
  language,
  setLanguage,
  searchQuery,
  setSearchQuery,
  savedCount,
  onOpenCurrencyConverter,
  onOpenWeekendPlanner,
}) => {
  const t = {
    de: {
      subtitle: 'Reiseführer & Fotospots',
      searchPlaceholder: 'Spot, Palast, Essen, Ort suchen...',
      listTab: 'Entdecken',
      mapTab: 'Karte',
      savedTab: 'Gemerkt',
      weekendPlannerTab: 'Events & Planer',
      currencyBtn: 'Währung (€ / ₩)',
      currencyShort: '€ ⇄ ₩',
      clearTooltip: 'Suche löschen',
      currencyTooltip: 'Währungsrechner EUR (€) ⇄ KRW (₩) öffnen',
    },
    en: {
      subtitle: 'Travel Guide & Photo Spots',
      searchPlaceholder: 'Search spots, palaces, food...',
      listTab: 'Explore',
      mapTab: 'Map',
      savedTab: 'Saved',
      weekendPlannerTab: 'Events & Trips',
      currencyBtn: 'Currency (€ / ₩)',
      currencyShort: '€ ⇄ ₩',
      clearTooltip: 'Clear search',
      currencyTooltip: 'Open Currency Converter EUR (€) ⇄ KRW (₩)',
    },
    kr: {
      subtitle: '한국 여행 & 명소 가이드',
      searchPlaceholder: '명소, 궁궐, 맛집, 지역 검색...',
      listTab: '탐색',
      mapTab: '지도',
      savedTab: '저장됨',
      weekendPlannerTab: '축제 & 주말코스',
      currencyBtn: '환율 계산기',
      currencyShort: '€ ⇄ ₩',
      clearTooltip: '검색어 지우기',
      currencyTooltip: '유로 (€) ⇄ 원화 (₩) 환율 계산기',
    },
  }[language];

  return (
    <header className="header">
      <div className="container header__inner">
        {/* Main Header Bar */}
        <div className="header__bar">
          {/* Logo */}
          <button
            onClick={() => setViewMode('list')}
            className="logo"
            aria-label="K-SpotFinder Home"
          >
            <div className="logo__icon">
              <Compass style={{ width: 20, height: 20 }} />
            </div>
            <div className="logo__text-group">
              <div className="logo__title-row">
                <span className="logo__title">K-SpotFinder</span>
                <span className="logo__badge">KOREA</span>
              </div>
              <span className="logo__subtitle">{t.subtitle}</span>
            </div>
          </button>

          {/* Desktop Nav Tabs */}
          <nav className="nav-tabs" aria-label="Hauptnavigation">
            <button
              onClick={() => setViewMode('list')}
              className={`nav-tab ${viewMode === 'list' ? 'nav-tab--active' : ''}`}
            >
              <Layers style={{ width: 16, height: 16 }} />
              <span>{t.listTab}</span>
            </button>

            <button
              onClick={() => setViewMode('map')}
              className={`nav-tab ${viewMode === 'map' ? 'nav-tab--active' : ''}`}
            >
              <MapPin style={{ width: 16, height: 16 }} />
              <span>{t.mapTab}</span>
            </button>

            <button
              onClick={() => setViewMode('saved')}
              className={`nav-tab nav-tab--saved ${viewMode === 'saved' ? 'nav-tab--active' : ''}`}
            >
              <Bookmark
                style={{
                  width: 16,
                  height: 16,
                  fill: savedCount > 0 && viewMode === 'saved' ? 'currentColor' : 'none',
                }}
              />
              <span>{t.savedTab}</span>
              {savedCount > 0 && (
                <span className="nav-tab__badge">{savedCount}</span>
              )}
            </button>

            <button
              onClick={onOpenWeekendPlanner}
              className="nav-tab nav-tab--events"
              title={t.weekendPlannerTab}
            >
              <CalendarDays style={{ width: 16, height: 16 }} />
              <span>{t.weekendPlannerTab}</span>
            </button>
          </nav>

          {/* Right: Search, Currency & Language */}
          <div className="header__actions">
            {/* Desktop Search */}
            <div className="search-box search-box--desktop">
              <Search className="search-box__icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="search-box__input"
                aria-label={t.searchPlaceholder}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="search-box__clear"
                  title={t.clearTooltip}
                  aria-label={t.clearTooltip}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Currency Converter Desktop Trigger Button */}
            <button
              onClick={onOpenCurrencyConverter}
              className="currency-header-btn currency-header-btn--desktop"
              title={t.currencyTooltip}
            >
              <Coins className="currency-header-btn__icon" />
              <span className="currency-header-btn__text currency-header-btn__text--full">
                {t.currencyBtn}
              </span>
              <span className="currency-header-btn__text currency-header-btn__text--short">
                {t.currencyShort}
              </span>
            </button>

            {/* Language Switcher */}
            <div className="lang-switcher" role="group" aria-label="Sprachauswahl">
              {(['de', 'en', 'kr'] as Language[]).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  className={`lang-btn ${language === lang ? 'lang-btn--active' : ''}`}
                  aria-pressed={language === lang}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Search & Currency Row */}
        <div className="mobile-search-row">
          <div className="search-box search-box--mobile">
            <Search className="search-box__icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="search-box__input"
              aria-label={t.searchPlaceholder}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="search-box__clear"
                title={t.clearTooltip}
                aria-label={t.clearTooltip}
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={onOpenCurrencyConverter}
            className="currency-mobile-btn"
            title={t.currencyTooltip}
            aria-label={t.currencyTooltip}
          >
            <Coins style={{ width: 14, height: 14 }} />
            <span>{t.currencyShort}</span>
          </button>
        </div>
      </div>
    </header>
  );
};
